<template>
  <v-container fluid class="onboarding-page">
    <div v-if="initializing" class="d-flex justify-center align-center py-12">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <div v-else-if="loadError" class="pa-6">
      <v-alert type="error" prominent>
        {{ loadErrorMessage }}
      </v-alert>
      <v-btn text color="primary" class="mt-4" @click="$router.push('/login')">
        Ir para o login
      </v-btn>
    </div>

    <template v-else>
      <div class="onboarding-page__header">
        <div>
          <h1 class="onboarding-page__title">Concluir cadastro</h1>
          <p class="onboarding-page__subtitle">
            Etapa {{ currentStepIndex + 1 }} de {{ totalSteps }}
          </p>
          <p class="onboarding-page__hint">
            Seus dados principais foram enviados pela franquia. Complete endereço e dados do
            usuário para acessar a plataforma.
          </p>
        </div>
      </div>

      <div class="auth-steps onboarding-page__progress" aria-hidden="true">
        <span
          v-for="(step, index) in stepOrder"
          :key="step"
          class="auth-steps__item"
          :class="{
            'auth-steps__item--active': currentStep === step,
            'auth-steps__item--done': currentStepIndex > index,
          }"
        />
      </div>

      <v-form ref="form" v-model="valid" lazy-validation class="onboarding-page__form">
        <v-window v-model="currentStep" class="mt-4">
          <v-window-item value="mainData">
            <p class="subtitle-2 mt-4">Dados principais</p>
            <v-row>
              <v-col cols="12" md="2">
                <v-select
                  :items="person"
                  v-model="partner.typeCompany"
                  label="Tipo de parceiro"
                  :disabled="loading"
                  item-text="text"
                  item-value="value"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  :label="partner.typeCompany == 'PJ' ? 'Razão social *' : 'Nome completo *'"
                  :required="true"
                  :disabled="loading"
                  v-model="partner.name"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  :label="
                    partner.typeCompany == 'PJ' ? 'Nome fantasia' : 'Como quer ser chamado'
                  "
                  :disabled="loading"
                  v-model="partner.nickName"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  :label="partner.typeCompany == 'PJ' ? 'CNPJ *' : 'CPF * '"
                  disabled
                  readonly
                  outlined
                  v-model="partner.document"
                  hint="Documento informado pela franquia"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  label="Telefone *"
                  :disabled="loading"
                  :required="true"
                  v-model="partner.phone"
                  outlined
                  return-masked-value
                  v-mask="
                    partner.phone.length <= 14 ? '(##) ####-####' : '(##) #####-####'
                  "
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="Email *"
                  :disabled="loading"
                  :required="true"
                  v-model="partner.email"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  label="Categoria *"
                  outlined
                  v-model="partner.categoryId"
                  :items="categories"
                  :disabled="loading"
                  item-text="name"
                  item-value="id"
                />
              </v-col>
            </v-row>
          </v-window-item>

          <v-window-item value="addressData">
            <p class="subtitle-2 mt-4">Dados de endereço</p>
            <v-row>
              <v-col cols="12" md="2">
                <v-text-field
                  label="CEP *"
                  :disabled="loading"
                  required="true"
                  v-model="partner.address.zipCode"
                  v-mask="'#####-###'"
                  outlined
                  @blur="handleAddress"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="Logradouro *"
                  :required="true"
                  :disabled="loading"
                  v-model="partner.address.street"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  label="Número"
                  :disabled="loading"
                  v-model="partner.address.addressNumber"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="Complemento"
                  :disabled="loading"
                  v-model="partner.address.complement"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="Bairro *"
                  :required="true"
                  :disabled="loading"
                  v-model="partner.address.neighborhood"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  label="UF *"
                  :required="true"
                  :disabled="true"
                  :readonly="true"
                  v-model="partner.address.state"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="Cidade *"
                  :required="true"
                  :disabled="true"
                  :readonly="true"
                  v-model="partner.address.cityName"
                  outlined
                />
              </v-col>
            </v-row>
          </v-window-item>

          <v-window-item value="userData">
            <p class="subtitle-2 mt-4">Dados do usuário</p>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field outlined label="Nome completo *" required="true" v-model="partner.user.name" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  outlined
                  label="Email *"
                  :disabled="loading"
                  required="true"
                  v-model="partner.user.email"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  label="Celular *"
                  @input="handleMaskPhone"
                  required="true"
                  :disabled="loading"
                  v-model="partner.user.phone"
                  maxlength="15"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  label="CPF *"
                  @input="formatCPF"
                  minLength="14"
                  maxLength="14"
                  :rules="rules"
                  :disabled="loading"
                  required="true"
                  v-model="partner.user.document"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  outlined
                  label="Nome da mãe *"
                  v-model="partner.user.motherName"
                  required="true"
                  :disabled="loading"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  label="Data de nascimento *"
                  v-model="partner.user.birthdate"
                  v-mask="'##/##/####'"
                  required="true"
                  :disabled="loading"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="show ? 'text' : 'password'"
                  required="true"
                  v-model="partner.user.password"
                  name="input-10-2"
                  label="Senha *"
                  class="input-group--focused"
                  @click:append="show = !show"
                />
              </v-col>
            </v-row>
          </v-window-item>
        </v-window>
        <v-row class="onboarding-page__actions">
          <v-col cols="6" class="text-left">
            <v-btn v-if="currentStep !== 'mainData'" text @click="handleBack">Voltar</v-btn>
            <v-btn v-else text @click="$router.push('/login')">Cancelar</v-btn>
          </v-col>
          <v-col cols="6" class="text-right">
            <v-btn
              v-if="currentStep !== 'userData'"
              color="primary"
              dark
              depressed
              @click="handleNext"
            >
              Próximo
            </v-btn>
            <v-btn
              v-else
              color="primary"
              dark
              depressed
              class="ml-4"
              :disabled="!handleIsFormValid()"
              @click="handleSave"
            >
              Concluir cadastro
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { GetAddress } from "@/services/viaCep/GetAddress";
import { GetAllCategories } from "@/services/partners/GetAllCategories";
import { GetRegistrateDraft } from "@/services/registrate/GetRegistrateDraft";
import { CompleteRegistrate } from "@/services/registrate/CompleteRegistrate";

import { PartnerDetailsType } from "@/types/partner/Partner";

function formatDocumentDisplay(document: string): string {
  const d = document.replace(/\D/g, "");
  if (d.length === 11) {
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (d.length === 14) {
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return document;
}

function formatPhoneDisplay(phone: string): string {
  const p = phone.replace(/\D/g, "");
  if (p.length <= 10) {
    return p.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
  }
  return p.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
}

export default defineComponent({
  computed: {
    stepOrder(): string[] {
      return Object.keys(this.steps);
    },
    currentStepIndex(): number {
      return this.stepOrder.indexOf(this.currentStep);
    },
    totalSteps(): number {
      return this.stepOrder.length;
    },
  },

  data() {
    return {
      initializing: true,
      draftLoaded: false,
      loadError: false,
      loadErrorMessage: "",
      rules: [(v: string) => (v && v.length >= 14) || "CPF inválido"],
      currentStep: "mainData",
      steps: {
        mainData: "Dados principais",
        addressData: "Dados de endereço",
        userData: "Dados do usuário",
      },
      valid: true,
      loading: false,
      show: false,
      person: [
        { id: 1, text: "Pessoa Fisica", value: "PF" },
        { id: 2, text: "Pessoa Jurídica", value: "PJ" },
      ],
      categories: [] as { id: number; name: string }[],
      partner: {
        id: "",
        name: "",
        status: "PENDING",
        nickName: "",
        tax: 5,
        typeCompany: "PJ",
        category: { name: "", id: "" },
        phone: "",
        email: "",
        document: "",
        pixkey: " ",
        cnaeCode: "",
        useAssociateTax: true,
        address: {
          state: "",
          street: "",
          addressNumber: "",
          neighborhood: "",
          complement: "",
          zipCode: "",
          cityName: "",
          ibge: "",
        },
        users: [],
        user: {
          name: "",
          email: "",
          phone: "",
          document: "",
          password: "",
          nickname: "",
          birthdate: "",
          motherName: "",
          backDocumentImage: "",
          frontDocumentImage: "",
          selfImage: "",
        },
        associate: [],
        businessHours: [],
        createdAt: "",
        digitalAccountNumber: "",
        digitalAccountDocument: "",
        associates: [],
        categoryId: "",
        contractFile: "",
        registrationStatusFile: "",
        partnerConfigs: {
          id: 0,
          onlineSale: false,
          pdvSale: false,
          updatedAt: "",
          instantCashback: 0,
          greenBuy: false,
        },
        description: "",
        instagram: "",
        whatsapp: "",
      } as PartnerDetailsType,
    };
  },

  methods: {
    handleIsFormValid(): boolean {
      return (
        this.handleIsValidMainData() &&
        this.handleIsValidAddressData() &&
        this.handleIsValidUserData()
      );
    },
    handleIsValidMainData(): boolean {
      const cat = this.partner.categoryId;
      const hasCategory =
        cat !== "" && cat !== null && cat !== undefined && String(cat).length > 0;
      return (
        this.partner.name.length > 0 &&
        this.partner.document.replace(/\D/g, "").length > 0 &&
        this.partner.phone.length > 0 &&
        this.partner.email.length > 0 &&
        hasCategory
      );
    },
    handleIsValidAddressData(): boolean {
      return (
        this.partner.address.zipCode.replace(/\D/g, "").length > 0 &&
        this.partner.address.street.length > 0 &&
        this.partner.address.neighborhood.length > 0 &&
        this.partner.address.state.length > 0 &&
        this.partner.address.cityName.length > 0 &&
        this.partner.address.ibge.length > 0
      );
    },
    handleIsValidUserData(): boolean {
      return (
        this.partner.user.name.length > 0 &&
        this.partner.user.email.length > 0 &&
        this.partner.user.phone.replace(/\D/g, "").length > 0 &&
        this.partner.user.document.replace(/\D/g, "").length === 11 &&
        this.partner.user.password.length > 0 &&
        this.partner.user.birthdate.length > 0 &&
        this.partner.user.motherName.length > 0
      );
    },

    async handleAddress(): Promise<void> {
      const zip = this.partner.address.zipCode.replace(/\D/g, "");
      if (zip.length < 8) return;
      const getAddress = new GetAddress();
      const address = await getAddress.execute(this.partner.address.zipCode);
      if (address) {
        this.partner.address.street = address.logradouro || "";
        this.partner.address.neighborhood = address.bairro || "";
        this.partner.address.cityName = address.localidade || "";
        this.partner.address.state = address.uf || "";
        this.partner.address.ibge = address.ibge || "";
      }
    },

    handleSave(): void {
      this.loading = true;
      const partnerId = this.$route.params.partnerId as string;
      const complete = new CompleteRegistrate();
      complete
        .execute(partnerId, this.partner)
        .then(() => {
          this.$store.dispatch("showSnack", {
            message: "Cadastro concluído! Faça login com seu CPF e senha.",
            color: "green darken-1",
          });
          this.$router.push("/login");
        })
        .catch((err) => {
          const msg =
            err?.response?.data?.message ||
            "Não foi possível concluir o cadastro. Verifique os dados e tente novamente.";
          this.$store.dispatch("showSnack", {
            message: Array.isArray(msg) ? msg.join(", ") : String(msg),
            color: "red",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async handleGetAllCategories(): Promise<void> {
      const categories = new GetAllCategories();
      const dataCategories = await categories.execute();
      this.categories = dataCategories.data;
    },

    handleMaskPhone(): void {
      this.partner.user.phone = this.partner.user.phone.replace(/\D/g, "");
      this.partner.user.phone = this.partner.user.phone
        .replace(/^(\d)/, "($1")
        .replace(/^(\(\d{2})(\d)/, "$1) $2")
        .replace(/(\d{5})(\d{1,5})/, "$1-$2")
        .replace(/(-\d{4})\d+?$/, "$1");
    },

    formatCPF(): void {
      let cpf = this.partner.user.document.replace(/\D/g, "");
      if (cpf.length > 3) {
        cpf = cpf.substring(0, 3) + "." + cpf.substring(3);
      }
      if (cpf.length > 7) {
        cpf = cpf.substring(0, 7) + "." + cpf.substring(7);
      }
      if (cpf.length > 11) {
        cpf = cpf.substring(0, 11) + "-" + cpf.substring(11, 13);
      }
      this.partner.user.document = cpf;
    },

    handleNext(): void {
      if (this.currentStep === "mainData") {
        if (this.handleIsValidMainData()) {
          this.currentStep = "addressData";
        } else {
          this.$store.dispatch("showSnack", {
            message: "Preencha todos os dados principais antes de continuar.",
            color: "red",
          });
        }
      } else if (this.currentStep === "addressData") {
        if (this.handleIsValidAddressData()) {
          this.currentStep = "userData";
        } else {
          this.$store.dispatch("showSnack", {
            message: "Preencha todos os dados de endereço antes de continuar.",
            color: "red",
          });
        }
      }
    },
    handleBack(): void {
      if (this.currentStep === "userData") {
        this.currentStep = "addressData";
      } else if (this.currentStep === "addressData") {
        this.currentStep = "mainData";
      }
    },

    async loadDraft(): Promise<void> {
      const partnerId = this.$route.params.partnerId as string;
      if (!partnerId) {
        this.loadError = true;
        this.loadErrorMessage = "Link inválido.";
        return;
      }
      try {
        const draft = await new GetRegistrateDraft().execute(partnerId);
        this.partner.id = draft.id;
        this.partner.name = draft.name;
        this.partner.email = draft.email;
        this.partner.phone = formatPhoneDisplay(draft.phone);
        this.partner.document = formatDocumentDisplay(draft.document);
        this.partner.typeCompany = draft.typeCompany;
        this.partner.nickName = draft.nickName || "";
        this.partner.useAssociateTax = draft.useAssociateTax !== false;
        this.partner.user.email = draft.email;
        this.partner.user.phone = formatPhoneDisplay(draft.phone);
        this.draftLoaded = true;
      } catch (e: unknown) {
        this.loadError = true;
        const err = e as { response?: { data?: { message?: string | string[] } } };
        const m = err?.response?.data?.message;
        this.loadErrorMessage = Array.isArray(m)
          ? m.join(", ")
          : m || "Não foi possível carregar o cadastro. O link pode ter expirado ou já foi utilizado.";
      } finally {
        this.initializing = false;
      }
    },
  },

  async mounted(): Promise<void> {
    await this.handleGetAllCategories();
    await this.loadDraft();
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.onboarding-page {
  min-height: 100vh;
  background: $br-bg;
  padding: 24px 16px 40px;
}

.onboarding-page__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: $br-ink-soft;
}

.onboarding-page__subtitle {
  margin: 6px 0 0;
  color: $br-muted;
  font-size: $br-font-size-md;
}

.onboarding-page__hint {
  margin: 8px 0 0;
  color: $br-muted;
  font-size: $br-font-size-sm;
  line-height: 1.45;
}

.onboarding-page__progress {
  margin: 20px 0 8px;
}

.onboarding-page__form {
  margin-top: 8px;
}

.onboarding-page__actions {
  margin-top: 8px;
}
</style>
